// All dates render in WAT (West Africa Time, UTC+1)
const TZ = "Africa/Lagos";

export function formatCurrency(amount: number, currency: "USD" | "NGN" = "USD"): string {
  if (currency === "NGN") {
    return "₦" + amount.toLocaleString("en-NG");
  }
  if (amount >= 1_000_000) {
    return "$" + (amount / 1_000_000).toFixed(1) + "M";
  }
  if (amount >= 1_000) {
    return "$" + amount.toLocaleString("en-US");
  }
  return "$" + amount.toString();
}

export function formatTime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

export function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

export function truncateHash(hash: string, chars: number = 6): string {
  if (hash.length <= chars * 2 + 2) return hash;
  return hash.slice(0, chars + 2) + "..." + hash.slice(-chars);
}

export function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: TZ });
  const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: TZ });
  return `${date} ${time}`;
}

export function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: TZ,
  });
}

export function formatDateCompact(dateStr: string): string {
  const d = new Date(dateStr);
  // Use Intl to get WAT parts
  const parts = new Intl.DateTimeFormat("en-GB", {
    month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false, timeZone: TZ,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || "00";
  return `${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
}

export function stars(score: number): string {
  const full = Math.floor(score);
  const remainder = score - full;
  let s = "★".repeat(full);
  if (remainder >= 0.5) s += "½";
  const empty = 5 - Math.ceil(score);
  s += "☆".repeat(Math.max(0, empty));
  return s;
}

export function slugify(input: string): string {
  return input.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Nigerian bank options for payer detail dropdowns */
export const NIGERIAN_BANKS = [
  "GTBank", "Access Bank", "Zenith Bank", "UBA", "First Bank",
  "Fidelity Bank", "Union Bank", "Stanbic IBTC", "Polaris Bank", "FCMB",
  "Ecobank", "Heritage Bank", "Globus Bank", "Titan Trust Bank",
  "TAJ Bank", "SunTrust Bank",
  "OPay", "Kuda", "PalmPay", "Nomba", "Wema Bank", "Sterling Bank",
  "Moniepoint", "Providus Bank", "Keystone Bank", "Unity Bank",
  "Carbon", "FairMoney", "Other",
] as const;

/** Copy text to clipboard — works on HTTP (non-secure) contexts too */
export function copyText(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback for non-HTTPS
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
  return Promise.resolve();
}
